import { useCallback, useEffect, useState } from "react";
import { api, API, tokenStore } from "../../lib/api";
import {
  Download, RefreshCw, Trash2, Search, RotateCw, Mail,
  AlertCircle, CheckCircle2, ChevronLeft, ChevronRight,
} from "lucide-react";

const PER_PAGE = 50;

const STATUS_META = {
  subscribed: { label: "Sincronizado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pendiente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  error: { label: "Error", cls: "bg-red-50 text-red-700 border-red-200" },
  auth_error: { label: "Error auth", cls: "bg-red-50 text-red-700 border-red-200" },
};

const LANG_LABEL = { es: "ES", fr: "FR" };

export default function NewsletterAdmin() {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, subscribed: 0, pending: 0, error: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/newsletter/subscribers", {
        params: { page, per_page: PER_PAGE, q, status },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setCounts(data.counts || { all: 0, subscribed: 0, pending: 0, error: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, q, status]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const applyFilter = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };
  const applySearch = (val) => {
    setQ(val);
    setPage(1);
  };

  const retry = async (id) => {
    setBusyId(id);
    setToast(null);
    try {
      await api.post(`/admin/newsletter/subscribers/${id}/retry`);
      setToast({ kind: "ok", msg: "Sincronizado con Acumbamail." });
      await load();
    } catch (e) {
      setToast({ kind: "err", msg: e.response?.data?.detail || "Error al reintentar." });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este suscriptor?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/newsletter/subscribers/${id}`);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    fetch(`${API}/admin/newsletter/export.csv`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `newsletter_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div data-testid="cms-newsletter">
      <div className="flex justify-between items-start mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040] flex items-center gap-2">
            <Mail className="w-5 h-5" /> Newsletter
          </h1>
          <p className="text-sm text-gray-600">Suscriptores del formulario público, sincronizados con Acumbamail.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="newsletter-refresh"
            className="px-3 py-2 text-sm border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-4 h-4" /> Refrescar
          </button>
          <button
            onClick={exportCsv}
            data-testid="newsletter-export"
            className="bg-[#0F2040] text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-[#1A2F5C]"
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-4 p-3 text-sm border flex items-center gap-2 ${
            toast.kind === "ok"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
          data-testid="newsletter-toast"
        >
          {toast.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="newsletter-filter">
        <FilterBtn active={status === ""} onClick={() => applyFilter("")} label={`Todos (${counts.all})`} testid="filter-all" />
        <FilterBtn active={status === "subscribed"} onClick={() => applyFilter("subscribed")} label={`Sincronizados (${counts.subscribed})`} testid="filter-subscribed" />
        <FilterBtn active={status === "pending"} onClick={() => applyFilter("pending")} label={`Pendientes (${counts.pending})`} testid="filter-pending" />
        <FilterBtn active={status === "error"} onClick={() => applyFilter("error")} label={`Errores (${counts.error})`} testid="filter-error" />
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Buscar por email o nombre…"
          value={q}
          onChange={(e) => applySearch(e.target.value)}
          className="w-full max-w-md pl-9 pr-3 py-2 border border-gray-200 focus:border-[#0F2040] outline-none text-sm"
          data-testid="newsletter-search"
        />
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-600">
            <tr>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Nombre</th>
              <th className="text-left px-5 py-3 w-16">Idioma</th>
              <th className="text-left px-5 py-3">Estado</th>
              <th className="text-left px-5 py-3">Alta</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400" data-testid="newsletter-empty">
                  {counts.all === 0 ? "Todavía no hay suscriptores." : "Ningún suscriptor coincide con el filtro."}
                </td>
              </tr>
            ) : (
              items.map((s) => {
                const st = s.acumbamail_status || "pending";
                const meta = STATUS_META[st] || STATUS_META.pending;
                const isFailed = st === "error" || st === "auth_error" || st === "pending";
                return (
                  <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`newsletter-row-${s.id}`}>
                    <td className="px-5 py-3 font-medium text-[#0F2040]">{s.email}</td>
                    <td className="px-5 py-3 text-gray-600">{s.name || "—"}</td>
                    <td className="px-5 py-3 text-xs text-gray-500">{LANG_LABEL[s.lang] || (s.lang || "—").toUpperCase()}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 border ${meta.cls}`}>{meta.label}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-500">{formatDate(s.created_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {isFailed && (
                        <button
                          onClick={() => retry(s.id)}
                          disabled={busyId === s.id}
                          data-testid={`newsletter-retry-${s.id}`}
                          className="px-3 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1.5 mr-2 disabled:opacity-50"
                          title="Reintentar sincronización con Acumbamail"
                        >
                          <RotateCw className={`w-3 h-3 ${busyId === s.id ? "animate-spin" : ""}`} /> Reintentar
                        </button>
                      )}
                      <button
                        onClick={() => remove(s.id)}
                        disabled={busyId === s.id}
                        data-testid={`newsletter-delete-${s.id}`}
                        className="p-1.5 text-gray-400 hover:text-red-600 inline-block align-middle disabled:opacity-50"
                        title="Eliminar suscriptor"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm" data-testid="newsletter-pagination">
        <p className="text-gray-500">
          {total === 0 ? "0 suscriptores" : `Página ${page} de ${totalPages} · ${total} suscriptores`}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="newsletter-prev-page"
            className="px-3 py-2 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            data-testid="newsletter-next-page"
            className="px-3 py-2 border border-gray-200 hover:border-[#0F2040] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Siguiente <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs font-medium border ${
        active ? "bg-[#0F2040] text-white border-[#0F2040]" : "border-gray-200 text-gray-700 hover:border-[#0F2040]"
      }`}
    >
      {label}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) +
      " " +
      d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
    );
  } catch {
    return iso;
  }
}
